import React, { useEffect, useRef, useState } from 'react';
import { Send, X } from 'lucide-react';
import ChatMessageBubble from './ChatMessageBubble';
import './StudentChatPanel.css';

export default function StudentChatPanel({
  messages,
  onSend,
  onClose,
  historyCount,
  onRead,
}) {
  const [input, setInput] = useState('');
  const [historyVisible, setHistoryVisible] = useState((historyCount ?? 0) > 0);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!historyCount) return;
    setHistoryVisible(true);
    const timeoutId = setTimeout(() => setHistoryVisible(false), 6000);
    return () => clearTimeout(timeoutId);
  }, [historyCount]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages?.length]);

  useEffect(() => {
    onRead?.();
  }, [onRead]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    onSend?.(text);
    setInput('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="student-chat-panel">
      <div className="student-chat-panel__header">
        <span>Chat with Tutor</span>
        <button className="student-chat-panel__close" onClick={onClose} aria-label="Close chat">
          <X size={16} />
        </button>
      </div>

      <div className="student-chat-panel__messages">
        {historyVisible && (historyCount ?? 0) > 0 && (
          <div className="student-chat-panel__history-banner">
            {historyCount} previous message{historyCount > 1 ? 's' : ''} from this session
          </div>
        )}
        {(messages ?? []).map((msg, index) => (
          <ChatMessageBubble
            key={msg?._id ?? `${msg?.timestamp ?? 'ts'}-${index}`}
            message={msg?.message ?? ''}
            timestamp={msg?.timestamp ?? msg?.createdAt}
            isOwn={msg?.senderType === 'student'}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="student-chat-panel__input-row">
        <textarea
          className="student-chat-panel__input"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          maxLength={500}
          rows={1}
        />
        <button
          className="student-chat-panel__send"
          onClick={handleSend}
          disabled={!input.trim()}
          aria-label="Send message"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
